import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import clone from 'clone';
import Popper from 'popper.js';
import {PopoverPlacement, PopoverTrigger} from '../popover/popover';
import {Icon} from '../icon/icon';
import {KeyboardCode} from '../table/table-body-cell';

require('../../../../icons/down.svg');
require('../../../../icons/up.svg');

export enum MatchType {
  any = 'any',
  start = 'start',
  end = 'end'
}

export enum AutoSelectType {
  exact = 'exact',
  any = 'any',
  start = 'start',
  end = 'end',
  off = 'off'
}

@Component({
  template: require('./select.html'),
  name: 'wc-select',
  components : {
    'wc-icon':Icon
  }
})
export class WcSelect extends Vue {
  @Prop({
    default: 'exact'
  })
  autoSelectMatch: string;

  @Prop()
  value: any;

  @Prop()
  data: Array<any>;

  @Prop()
  id: any;

  @Prop()
  text: string;

  @Prop()
  fixedText: string;

  @Prop({
    default: false
  })
  selectAll: boolean;

  @Prop({
    default: false
  })
  multiSelect: boolean;

  @Prop({
    default: PopoverTrigger.Click
  })
  trigger: PopoverTrigger;

  @Prop()
  className: string;

  @Prop({
    default: false
  })
  show: boolean;

  @Prop({
    default: 'value'
  })
  valueField: string;

  @Prop({
    default: MatchType.any
  })
  filterMatch: MatchType;

  @Prop({
    default: 'label'
  })
  labelField: string;

  @Prop({
    default: 'disabled'
  })
  disabledField: string;

  @Prop()
  onShow: () => void;

  @Prop()
  onHide: () => void;

  @Prop()
  labelComponent: string;

  @Prop({
    default: false
  })
  searchable: boolean;

  @Prop({
    default: false
  })
  borderless: boolean;

  @Prop({
    default: false
  })
  allowNull: boolean;

  @Prop({
    default: false
  })
  disabled: boolean;

  popper: Popper;
  delayOnMouseOut: number = 10;
  showPopover: boolean = false;
  timer: number;
  clickOutsideHandler: () => void;
  prevItem: any;
  selectAllChecked: boolean = false;
  cloneData: Array<any> = [];
  searchText: string = '';
  searchItems: Array<any> = [];
  selectionChanged: boolean = false;

  get allSelected(): boolean {
    return this.cloneData.every(x => { return x.wcSelected; });
  }

  get selectionCount(): number {
    return this.selectedItems.length;
  }

  get selectionLabel(): string {
    return this.selectedItems.map(x=> {return x[this.labelField]}).join(', ');
  }

  get selectedItems() {
    return this.cloneData.filter(x => { return x.wcSelected; });
  }

  @Watch('selectAll')
  selectAllChange() {
    if (this.selectAll) {
      this.selectAllItems(true);
    }
  }

  selectAllItems(selectAll: boolean): void {
    this.selectAllChecked = selectAll;
    if (this.multiSelect) {
      for (let x = 0; x < this.cloneData.length; x++) {
        this.cloneData[x].wcSelected = selectAll;
        this.cloneData.splice(x, 1, this.cloneData[x]);
      }
    }
    this.updateValue(true);
  }

  @Watch('cloneData')
  cloneDataChange() {
    if (this.cloneData.some((item) => !item.wcSelected)) this.selectAllChecked = false;
    else this.selectAllChecked = true;
  }

  @Watch('data')
  dataChange(val: Array<any>) {
    this.cloneData = clone(val);
    this.searchItems = this.cloneData;

    if (this.multiSelect && this.selectAll) {
      this.selectAllItems(this.selectAll);
      this.updateValue(true);
    }

    this.selectAllChecked = this.selectAll;
  }

  @Watch('placement')
  update() {
    this.createPopper();
  }

  @Watch('trigger')
  updateTrigger() {
    this.trigger = this.trigger;
  }

  @Watch('show')
  showUpdate() {
    this.showPopover = this.show;
  }

  @Watch('showPopover')
  updatePopover() {
    if (this.popper) {
      this.popper.update();
    }

    if (this.showPopover && this.onShow) {
      this.onShow.call(this);
    } else if (this.onHide) {
      this.onHide.call(this);
    }
  }

  selectItem(index: number) {
    this.selectionChanged = true;
    let item = this.searchItems[index];

    if (!this.allowNull && this.selectedItems.length === 1 && item === this.selectedItems[0]) {
      if(!this.multiSelect) this.hidePopover();
      else item.wcSelected = true;
      return;
    }

    if (!this.multiSelect && this.prevItem && this.prevItem != item) {
      this.prevItem.wcSelected = false;
    }

    this.prevItem = item;
    item.wcSelected = !item.wcSelected;

    this.searchItems.splice(index, 1, item);
    this.cloneData.splice(this.cloneData.indexOf(item), 1, item);

    this.$emit('selectChange');

    if(!this.multiSelect) this.hidePopover();
  }

  isMatchSelect():boolean {
    // handle exact | start | end | any
    return this.autoSelectMatch === AutoSelectType.exact && this.searchText.toLowerCase() === this.searchItems[0][this.labelField].toLowerCase() ||
      this.autoSelectMatch === AutoSelectType.start && this.searchText.toLowerCase().startsWith(this.searchItems[0][this.labelField].toLowerCase()) ||
      this.autoSelectMatch === AutoSelectType.end && this.searchText.toLowerCase().endsWith(this.searchItems[0][this.labelField].toLowerCase()) || 
      this.autoSelectMatch === AutoSelectType.any && this.searchText.toLowerCase().includes(this.searchItems[0][this.labelField].toLowerCase());
  }

  onSearchTextChange() {
    /* This search should match from the start position of the element in the dropdown list*/
    if (this.filterMatch == MatchType.start) {
      this.searchItems = this.cloneData.filter(x => x[this.labelField].toLowerCase().startsWith(this.searchText.toLowerCase()));
    } 
    /* This search should match from the end position of the element in the dropdown list*/ 
    else if (this.filterMatch == MatchType.end) {
      this.searchItems = this.cloneData.filter(x => x[this.labelField].toLowerCase().endsWith(this.searchText.toLowerCase()));
    } 
    /* This search should match from any position of the element in the dropdown list*/ 
    else {
      this.searchItems = this.cloneData.filter(x => x[this.labelField].toLowerCase().includes(this.searchText.toLowerCase()));
    }

    // sanity check before turning on feature
    if (this.autoSelectMatch !== AutoSelectType.off){
      // sanity check only for one item found/left
      if (this.searchItems && this.searchItems.length === 1){
        if (this.isMatchSelect()){
          this.selectItem(0);
        }
      }
    }
  }

  created() {
    this.dataChange(this.data);
    this.initialiseSelections();
  }

  @Watch('value')
  initialiseSelections() {
    if (!this.multiSelect) {
      this.initialiseValue(this.value);
    } else {
      for (let item of this.cloneData) {
        item.wcSelected = false;
      }
      for (let item of this.value) {
        this.initialiseValue(item);
      }
    }
  }

  initialiseValue(value: any, select: boolean = true) {
    let item = this.cloneData.find(x => x[this.valueField] === value);

    if (item) {
      if (!this.multiSelect && this.prevItem && this.prevItem != item) {
        this.prevItem.wcSelected = false;
        this.cloneData.splice(this.cloneData.indexOf(this.prevItem), 1, this.prevItem);
      }

      this.prevItem = item;
      item.wcSelected = select;
      this.cloneData.splice(this.cloneData.indexOf(item), 1, item);
    }
  }



  mounted() {
    if (this.trigger === PopoverTrigger.Static) this.createPopper();
  }

  createPopper() {
    let placement = PopoverPlacement.BottomStart;

    if (this.popper) this.popper.destroy(); 
    this.popper = new Popper((this.$refs.referenceElement as Element) as Element, this.$refs.popperElement as Element, {
      placement: placement,
      modifiers: {
        popperStyle: {
          enabled: true,
          fn: this.popperStyle.bind(this),
          order: 840,

        }
      }
    });

    if (this.trigger === PopoverTrigger.Static) {
      this.showPopover = true;
    }

    if (!this.clickOutsideHandler && this.trigger === PopoverTrigger.Click) {
      window.document.addEventListener('mousedown', this.outsideClick.bind(this), false);
    }
    
  }

  popperStyle(data: any) {
    // make sure popper is not being flipped before applying custom style
    if (data.placement.indexOf("bottom") !== -1 && !data.flipped)
      data.styles.top = '4px';

    if (data.placement.indexOf("top") !== -1 && !data.flipped) {
      data.styles.bottom = '4px';
    }

    // Check reference element exists
    if (!this.$refs.referenceElement)
      return;

    let width = Math.max((this.$refs.referenceElement as HTMLElement).offsetWidth, (this.$refs.selectItemsElement as HTMLElement).scrollWidth);
    
    data.styles.minWidth = width + 'px';

    return data;
  } 

  outsideClick(e: Event) {
     if (!this.$el || !this.$refs.referenceElement ||
      this.$el.contains(e.target as Node) ||
      (this.$refs.referenceElement as Element).contains(e.target  as Node) ||
      !this.$refs.popperElement || (this.$refs.popperElement as Element).contains(e.target  as Node)
    ) {
      return;
    }
    this.hidePopover();
  }

  hidePopover() {
    this.showPopover = false;
    this.updateValue();
  }

  updateValue(force?: boolean) {
    if (this.showPopover || force || this.selectionChanged) {
      if (this.multiSelect)
        this.$emit('input', this.selectedItems.map(x=> {return x[this.valueField]}));
      else
        this.$emit('input', this.selectedItems.length === 1 ? this.selectedItems[0][this.valueField] : undefined);
      this.selectionChanged = false;
    }
  }

  onMouseOver() {
    if (!this.popper) this.createPopper()

    if (this.trigger !== PopoverTrigger.Hover) return;
    this.showPopover = true;
    clearTimeout(this.timer);
  }

  onMouseOut() {
    if (this.trigger !== PopoverTrigger.Hover) return;

    this.timer = window.setTimeout(() => {
      this.showPopover = false;
    }, this.delayOnMouseOut);
  }

  onClick() {
    if (this.trigger !== PopoverTrigger.Click) return;

    this.showPopover = !this.showPopover;

    if (this.searchable) {
      this.$nextTick(() => (this.$refs.searchInput as HTMLElement).focus());
    }
  }

  accessibleOpen(e: KeyboardEvent){
    if (e && e.keyCode === KeyboardCode.F2) {
        // simulate click event to bypass custom event function check
        this.trigger = PopoverTrigger.Click;
        // call custom event function
        this.onClick();
    }
  }

  destroyed() {
    window.document.removeEventListener('mousedown', this.outsideClick.bind(this), false);
  }
}


Vue.directive('selectionLabel', function(el: any, binding) {
  
  if (el.parentElement)
    el.parentElement.classList.remove("label-count");
  if (binding.value.fixedText != null) {
    el.innerText = binding.value.fixedText;
  } else if (!binding.value.allSelected && binding.value.count === 0) {
    el.innerText = `Select ${binding.value.text}`;
  } else if (binding.value.allSelected && binding.value.count !== 1) {
    el.innerText = `All ${binding.value.text}`;
  } else if (binding.value.count > 0) {  
    let parentWidth;
    if(el.parentElement) {
      parentWidth = el.parentElement.scrollWidth;
    } else {
      parentWidth = 100;
    }
    el.innerText = binding.value.selectionLabel;

    // only use number for multi select, single will use ellipsis
    if (el.clientWidth > parentWidth && binding.value.multiSelect) {
      el.parentElement.classList.add("label-count");
      el.innerHTML = `<div class="wc-selection-label-count">${binding.value.count}</div> ${binding.value.text}`;
    }
  }
});




// WEBPACK FOOTER //
// src/../~/wc-lib/src/wc-ui/components/select/select.ts